import { useState, ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface LazySelectOption {
  value: string;
  label: string;
}

interface LazySelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: LazySelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * LazySelect - A performance-optimized Select component that defers rendering
 * of SelectContent (options) until the dropdown is opened for the first time.
 *
 * This significantly reduces initial render cost when displaying many Select
 * components on the page (e.g., in virtualized tables).
 *
 * Once opened, options remain in the DOM for faster subsequent opens.
 */
export function LazySelect({
  value,
  onValueChange,
  options,
  placeholder,
  className,
  disabled,
}: LazySelectProps) {
  const [hasOpened, setHasOpened] = useState(false);
  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      onOpenChange={(open) => {
        if (open) {
          setHasOpened(true);
        }
      }}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder}>{selectedLabel}</SelectValue>
      </SelectTrigger>
      {hasOpened && (
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      )}
    </Select>
  );
}
